import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  CANDIDATE_STATUSES,
  type CandidateFilterOptions,
  type CandidateFilters,
  type CandidateStatus,
} from "@/types/candidates"

/**
 * OKS-953 — the sentinel `value` for "2+ statuses are active".
 *
 * Matches NO `SelectItem`, deliberately, so no option carries a checkmark
 * while the filter rail owns the truth. `"all"` here would tick « Tou·te·s »
 * beside a trigger reading « 2 statuts sélectionnés », and the first active
 * status would tick one of several — both port the strip's "all tabs
 * unselected" ambiguity instead of fixing it (UX Spec §3).
 */
const MULTI_SENTINEL = ""

/**
 * OKS-17 — Status pipeline tabs (UX Spec §Status pipeline tabs).
 *
 * **All 9 enum values + "All" always render**, regardless of count
 * (CTPO lock — empty buckets show `(0)` and remain clickable so users
 * can transition into them once OKS-19 enables status changes).
 *
 * **Single-value shortcut on top of the multi-select Status filter:**
 * - 0 active statuses → "All" tab is the active one (no `?status[]=`).
 * - exactly 1 active status → that tab is the active one.
 * - 2+ active statuses → all tabs render unselected; the explicit
 *   filter rail's chip is the canonical truth.
 *
 * Counts come from `filter_options.statuses` — server-authoritative
 * (BE TRD locked: `[{value, count}]`). Counts are computed over the
 * brand-scoped relation IGNORING the active status filter (standard
 * facet UX so toggling a status doesn't make its own count vanish).
 *
 * Accessibility: `role="tablist"` on the strip, `role="tab"` +
 * `aria-selected` on each tab. Arrow-key navigation (left/right) is
 * provided by sequential focus order — shadcn `Tabs` would over-engineer
 * this for what is functionally a status-shortcut filter, not a true
 * tabbed view (the "tabs" don't swap content; they filter the same
 * table). Native `button` semantics + a roving tabindex would be the
 * proper richer pattern; for V1 we bind plain `<button role="tab">`
 * with `aria-selected` and keyboard navigation falls out of focus
 * order. UX Spec calls for arrow-key nav — handled in keyDown below.
 *
 * Locked testid contract:
 *   - `candidate-list-status-tabs` — tablist container
 *   - `candidate-list-status-tab-{status}` — each tab; status is one of
 *     `all`, `new`, `reviewed`, `approved`, `invited`, `joined`,
 *     `rejected`, `blocklisted`.
 */
interface CandidateStatusTabsProps {
  filters: Pick<CandidateFilters, "status">
  filterOptions: Pick<CandidateFilterOptions, "statuses">
  onSelect: (status: CandidateStatus | null) => void
  className?: string
}

export function CandidateStatusTabs({
  filters,
  filterOptions,
  onSelect,
  className,
}: CandidateStatusTabsProps) {
  const { t } = useTranslation()
  const activeStatuses = filters.status
  const activeStatus = activeStatuses.length === 1 ? activeStatuses[0] : null
  const isAllActive = activeStatuses.length === 0
  const stripRef = useRef<HTMLDivElement>(null)

  /**
   * OKS-948 — scroll the ACTIVE tab into view on mount and whenever the
   * active status changes.
   *
   * The strip went from 8 tabs to 10, which overflows a 375px row. Without
   * this, a user who filters by Rejected sees a strip scrolled to the left
   * with nothing selected — the UI silently disagrees with the results
   * below it.
   *
   * `block: "nearest"` keeps this HORIZONTAL only: `inline: "nearest"` does
   * the scrolling we want, and `block: "nearest"` stops the browser from
   * also scrolling the page vertically to reach the strip.
   *
   * ⚠️ Deliberately does NOT focus the tab. Moving focus on mount would
   * steal it from wherever the user actually is (UX Spec §6), and would
   * fight the roving-tabindex arrow-key navigation below.
   *
   * This is the MINIMUM mitigation. The structural fix — grouping or an
   * overflow menu — is OKS-953. Nothing here hard-codes a tab count or a
   * strip width, so that ticket is free to restructure.
   */
  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    const selected = strip.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    )
    // `scrollIntoView` is absent in jsdom and on very old browsers; the
    // strip stays usable without it, so degrade silently rather than throw.
    selected?.scrollIntoView?.({ block: "nearest", inline: "nearest" })
  }, [activeStatus, isAllActive])

  // Total count is the sum of all per-status counts (server-authoritative).
  const totalCount = filterOptions.statuses.reduce((sum, s) => sum + s.count, 0)

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement
    if (target.getAttribute("role") !== "tab") return
    const buttons = Array.from(
      target.parentElement?.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]',
      ) ?? [],
    )
    const idx = buttons.indexOf(target as HTMLButtonElement)
    if (idx < 0) return
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault()
      buttons[(idx + 1) % buttons.length]?.focus()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault()
      buttons[(idx - 1 + buttons.length) % buttons.length]?.focus()
    } else if (event.key === "Home") {
      event.preventDefault()
      buttons[0]?.focus()
    } else if (event.key === "End") {
      event.preventDefault()
      buttons[buttons.length - 1]?.focus()
    }
  }

  // OKS-953 — the trigger's three cases (UX Spec §2B). The 2+ case is the one
  // that matters: it must NEVER read as a single status's label.
  const selectValue = isAllActive
    ? "all"
    : activeStatuses.length === 1
      ? activeStatus!
      : MULTI_SENTINEL

  const triggerLabel = isAllActive
    ? `${t("candidates.list.tabs.all")} (${totalCount})`
    : activeStatuses.length === 1
      ? `${statusLabel(t, activeStatus!)} (${countFor(filterOptions.statuses, activeStatus!)})`
      : t("candidates.list.status_select.multi", {
          count: activeStatuses.length,
        })

  return (
    <>
      {/*
        OKS-953 — ≥640px: the shipped strip, unchanged, including OKS-948's
        scroll-active-into-view. Its testids are untouched — 25 references
        across 5 files depend on them, and `getByTestId` THROWS on a second
        match, so a collision reds every one of them with "Found multiple
        elements", which reads as a product bug rather than a naming clash.
      */}
      <div
        ref={stripRef}
        role="tablist"
        aria-label={t("candidates.list.tabs.all_aria")}
        data-testid="candidate-list-status-tabs"
        onKeyDown={handleKeyDown}
        className={cn(
          "hidden gap-1 overflow-x-auto border-b pb-px sm:flex",
          className,
        )}
      >
        <TabButton
          active={isAllActive}
          count={totalCount}
          label={t("candidates.list.tabs.all")}
          testId="candidate-list-status-tab-all"
          onClick={() => onSelect(null)}
        />
        {CANDIDATE_STATUSES.map((status) => {
          const count = countFor(filterOptions.statuses, status)
          return (
            <TabButton
              key={status}
              active={activeStatus === status}
              count={count}
              label={statusLabel(t, status)}
              testId={`candidate-list-status-tab-${status}`}
              onClick={() => onSelect(status)}
            />
          )
        })}
      </div>

      {/*
        <640px: a full-width Select. Ten tabs overflow a 375px row and the
        strip shows ~2.5 of 10 counts at any instant, never all ten; the opened
        select shows ten of ten. It does not hide the counts — it defers them
        by one tap and, in exchange, makes comparison possible on a phone.

        🔴 The swap is CSS ONLY (`hidden sm:flex` / `sm:hidden`), so BOTH
        controls are in the DOM at EVERY viewport. A JS media-query hook would
        mismatch under Inertia SSR. That is why this control carries its OWN
        testids and must never reuse `candidate-list-status-tab-{status}`.
      */}
      <div className={cn("sm:hidden", className)}>
        <Label
          htmlFor="candidate-list-status-select"
          className="text-muted-foreground mb-1 block text-xs"
        >
          {t("candidates.list.status_select.label")}
        </Label>
        <Select
          value={selectValue}
          // Base UI types the callback value as `string | null`. `null` and
          // the "all" item mean the same thing here — no status filter — and
          // both must map to `onSelect(null)`, which is the strip's existing
          // single-value shortcut contract.
          onValueChange={(value: string | null) =>
            onSelect(
              value === null || value === "all"
                ? null
                : (value as CandidateStatus),
            )
          }
        >
          <SelectTrigger
            id="candidate-list-status-select"
            data-testid="candidate-list-status-select"
            aria-label={t("candidates.list.status_select.aria")}
            // ≥44px touch target (UX Spec §5), full width at 320px.
            className="min-h-11 w-full"
          >
            {/*
              Base UI's `Select.Value` renders the RAW value by default, not the
              selected item's label. The render function supplies the label —
              and is also what lets the 2+ case show a count instead of a
              status, which no value→label map could express.
            */}
            <SelectValue>{() => triggerLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="all"
              data-testid="candidate-list-status-option-all"
            >
              {`${t("candidates.list.tabs.all")} (${totalCount})`}
            </SelectItem>
            {CANDIDATE_STATUSES.map((status) => {
              const count = countFor(filterOptions.statuses, status)
              return (
                <SelectItem
                  key={status}
                  value={status}
                  data-testid={`candidate-list-status-option-${status}`}
                  // Zero-buckets stay SELECTABLE per the CTPO lock; dimmed
                  // only, exactly as the strip's zero tabs are.
                  className={cn(count === 0 && "opacity-60")}
                >
                  {`${statusLabel(t, status)} (${count})`}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

function countFor(
  statuses: CandidateFilterOptions["statuses"],
  status: CandidateStatus,
): number {
  return statuses.find((s) => s.value === status)?.count ?? 0
}

function statusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: CandidateStatus,
): string {
  switch (status) {
    case "new":
      return t("candidates.status.new")
    case "reviewed":
      return t("candidates.status.reviewed")
    case "pending_brand_validation":
      return t("candidates.status.pending_brand_validation")
    case "approved":
      return t("candidates.status.approved")
    case "contacted":
      return t("candidates.status.contacted")
    case "invited":
      return t("candidates.status.invited")
    case "joined":
      return t("candidates.status.joined")
    case "rejected":
      return t("candidates.status.rejected")
    case "blocklisted":
      return t("candidates.status.blocklisted")
  }
}

interface TabButtonProps {
  active: boolean
  count: number
  label: string
  testId: string
  onClick: () => void
}

function TabButton({ active, count, label, testId, onClick }: TabButtonProps) {
  const isZeroBucket = count === 0
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      data-testid={testId}
      data-state={active ? "active" : "inactive"}
      data-count={count}
      onClick={onClick}
      className={cn(
        "shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        active
          ? "border-primary text-foreground"
          : "text-muted-foreground hover:text-foreground border-transparent",
        // Zero-buckets stay clickable per CTPO lock; visually dimmed.
        isZeroBucket && !active && "opacity-60",
        // Mobile touch target ≥44px height.
        "max-sm:py-3",
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "ml-1.5 inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-xs tabular-nums",
          active
            ? "bg-primary/10 text-foreground"
            : "bg-muted text-muted-foreground",
        )}
        aria-hidden="true"
      >
        {count}
      </span>
    </button>
  )
}
